import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  EyeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import InlineEditor from './InlineEditor';

const ProductRow = memo(({
  product,
  categories,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onToggleFeatured,
  inlineEdit
}) => {
  const {
    isEditing,
    startEdit,
    cancelEdit,
    saveEdit,
    editingValue,
    setEditingValue,
    editingLoading,
    editingErrors
  } = inlineEdit;

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      draft: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      active: 'פעיל',
      inactive: 'לא פעיל',
      draft: 'טיוטה'
    };
    const badgeStatus = product.is_active ? 'active' : 'inactive';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badges[badgeStatus]}`}>
        {labels[badgeStatus]}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const categoryOptions = [
    { value: '', label: 'ללא קטגוריה' },
    ...categories.map(cat => ({ value: cat.id, label: cat.name }))
  ];

  const isLowStock = product.stock_quantity <= (product.low_stock_threshold || 10);

  return (
    <tr className={`group hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/30 transition-all duration-200 ${isSelected ? 'bg-blue-50/30' : ''}`}>
      {/* Checkbox */}
      <td className="px-4 py-3 whitespace-nowrap">
        <input
          type="checkbox"
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 hover:border-blue-400 transition-colors cursor-pointer"
          checked={isSelected}
          onChange={() => onSelect(product.id)}
        />
      </td>

      {/* Product Name & Image */}
      <td className="px-4 py-3">
        <div className="flex items-center min-w-[250px]">
          <div className="flex-shrink-0 h-16 w-16 relative">
            {product.images && product.images.length > 0 ? (
              <>
                <img
                  className="h-16 w-16 rounded-lg object-cover shadow-sm group-hover:shadow-md transition-all group-hover:scale-105 ring-2 ring-gray-200 group-hover:ring-blue-300"
                  src={
                    product.images[0].startsWith('http')
                      ? `${product.images[0]}?id=${product.id}`
                      : `/api${product.images[0]}?id=${product.id}`
                  }
                  alt={product.name}
                  loading="lazy"
                  onError={(e) => {
                    e.target.src =
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAzMkMyOC40MTgzIDMyIDMyIDI4LjQxODMgMzIgMjRDMzIgMTkuNTgxNyAyOC40MTgzIDE2IDI0IDE2QzE5LjU4MTcgMTYgMTYgMTkuNTgxNyAxNiAyNEMxNiAyOC40MTgzIDE5LjU4MTcgMzIgMjQgMzJaIiBzdHJva2U9IiM5Q0EzQUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xNiAzNkwxOSAzM0wyNCAzOEwyOSAzM0wzMiAzNiIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
                  }}
                />
                {product.is_featured && (
                  <div className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-400 to-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center shadow-md">
                    🔥
                  </div>
                )}
                {/* NEW Badge */}
                {product.createdAt && new Date(product.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
                  <div className="absolute -top-1 -left-1 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-[9px] font-bold px-1 py-0.5 rounded shadow-md">
                    NEW
                  </div>
                )}
              </>
            ) : (
              <div className="h-16 w-16 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm">
                <PhotoIcon className="h-7 w-7 text-gray-400" />
              </div>
            )}
          </div>
          <div className="mr-3 flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors truncate">
              {product.name}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{product.sku}</div>
            {product.brand && product.brand !== 'null' && product.brand.trim() !== '' && (
              <div className="text-[10px] text-blue-600 mt-0.5 font-medium">🏷️ {product.brand}</div>
            )}
          </div>
        </div>
      </td>

      {/* Category */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="text-xs text-gray-600">{product.category?.name || 'ללא קטגוריה'}</span>
      </td>

      {/* Prices - Combined */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-col gap-1">
          {/* B2C Price */}
          <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 rounded border border-green-200">
            <span className="text-green-700 font-bold text-xs">
              ₪{(parseFloat(product.price_b2c) || 0).toFixed(0)}
            </span>
            <span className="text-[9px] text-green-600">B2C</span>
          </div>
          {/* B2B Price */}
          <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 rounded border border-blue-200">
            <span className="text-blue-700 font-bold text-xs">
              ₪{(parseFloat(product.price_b2b) || 0).toFixed(0)}
            </span>
            <span className="text-[9px] text-blue-600">B2B</span>
            {/* Discount Badge */}
            {product.price_b2b && product.price_b2c && product.price_b2b < product.price_b2c && (
              <span className="text-[9px] text-green-600 font-bold">
                (-{Math.round(((product.price_b2c - product.price_b2b) / product.price_b2c) * 100)}%)
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Stock */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
            {product.stock_quantity}
          </span>
          {isLowStock ? (
            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 animate-pulse" />
          ) : (
            <span className="text-green-500">✓</span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3 whitespace-nowrap">
        {getStatusBadge()}
      </td>

      {/* Featured/Trending */}
      <td className="px-4 py-3 whitespace-nowrap">
        <button
          onClick={() => onToggleFeatured(product.id, product.is_featured)}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all ${
            product.is_featured
              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={product.is_featured ? 'מסומן כטרנד' : 'סמן כטרנד'}
        >
          <span className="text-sm">{product.is_featured ? '🔥' : '➕'}</span>
        </button>
      </td>

      {/* Actions */}
      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
        <div className="flex gap-1.5 items-center">
          <button
            onClick={() => onEdit(product)}
            className="p-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md"
            title="ערוך מוצר"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <Link
            to={`/admin/products/${product.id}`}
            className="p-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-all"
            title="צפה במוצר"
          >
            <EyeIcon className="h-4 w-4" />
          </Link>
          <button
            onClick={() => onDelete(product)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-all"
            title="מחק מוצר"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
});

ProductRow.displayName = 'ProductRow';

export default ProductRow;

